import {BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject} from 'rxjs';
import {
  DynamicFormConfig,
  DynamicFormReadyEvent,
  FilterSelectFieldConfig,
  OptionSourceData,
  SelectItem
} from '@mydocma/dynamic-form';
import {TranslateService} from '@ngx-translate/core';
import {
  descriptionConfig,
  detailFormConfig,
  favoritesConfig, favoritesConfigUser,
  nameConfig,
  OptionFavoriteValue,
  OptionViewPointValue,
  overviewLayoutConfig,
  typeConfig,
  visibleForConfig
} from '../layout-edit-details/layout-edit-details.config';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Injectable, OnDestroy} from '@angular/core';
import {Layout} from '@docma-mm-core/layout/layout';
import {MultiOptionConfig} from '@mydocma/dynamic-form/lib/fields/multi-option/multi-option.config';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {LayoutFieldExt} from '@docma-mm-core/layout/layout-field';
import {User} from '@docma-mm-core/user/user';
import {LayoutFilter} from '@docma-mm-core/layout/layout-filter';
import {Project} from '@docma-mm-core/project/project';
import {ActionPermissionEnum} from '@docma-mm-core/permission/action-permission.enum';
import {MmProjectPermissionsService} from '../../../../mm-project/mm-project-permissions-service';
import {map, tap} from 'rxjs/operators';
import { ProjectActionPermissionEnum } from '@docma-mm-core/permission/project-action-permission.enum';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';

@UntilDestroy()
@Injectable()
export class LayoutEditDetailsService implements OnDestroy {
  public hasWideLabels$ = new BehaviorSubject<boolean>(false);

  editArgs: {Layout: Layout, Fields: LayoutFieldExt[], Filters: LayoutFilter[], IsNew: boolean, project: Project, user: User, hasReferences: boolean};
  layout: Layout;

  onTypeChanged = new Subject<EntityScopeEnum>();

  formConfig$ = new BehaviorSubject<DynamicFormConfig>(null);
  valueChanged = new Subject();

  hasErrorsSubject = new Subject<boolean>();
  hasErrors$ = this.hasErrorsSubject.asObservable();
  public onOverviewLayoutChanged = new Subject<boolean>();

  permissionEditGlobalLayout$ = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditGlobalLayout]);
  permissionEditProjectLayout$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditProjectLayout]);
  permissionEditLayoutVisibility$ = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditLayoutVisibility]);

  private detailsFormServices: DynamicFormReadyEvent;
  private attachedSubject = new BehaviorSubject<boolean>(false);
  private firstLoad = true;
  private hasEditLayoutVisibility = false;

  constructor(private readonly translateService: TranslateService,
              public readonly permissionService: MmProjectPermissionsService,
              private readonly dialogService: PromptDialogService) {
    this.formConfig$.next(detailFormConfig);
  }

  ngOnDestroy(): void {
  }

  public async attachToForm(event: DynamicFormReadyEvent) {
    this.detailsFormServices = event;

    this.permissionEditLayoutVisibility$
      .pipe(tap(perm => this.hasEditLayoutVisibility = perm)).toPromise();

    // const visibleForConfigUpdate: MultiOptionConfig = { ...visibleForConfig};
    // visibleForConfigUpdate.hidden = !this.hasEditLayoutVisibility;
    // this.detailsFormServices.dynamicFormService.updateFieldConfig(visibleForConfigUpdate);

    this.attachedSubject.next(true);
  }

  public async preInitialize(scope: EntityScopeEnum) {
    const visibleForConfigUpdate: MultiOptionConfig = { ...visibleForConfig};
    visibleForConfigUpdate.hidden = !this.hasEditLayoutVisibility || scope === EntityScopeEnum.User;
    this.detailsFormServices.dynamicFormService.updateFieldConfig(visibleForConfigUpdate);

    const isScopeUser: boolean = (scope === +EntityScopeEnum.User);

    const favoritesConfigUserUpdate: MultiOptionConfig = {...favoritesConfigUser};
    favoritesConfigUserUpdate.hidden = (isScopeUser ? false : true);
    this.detailsFormServices.dynamicFormService.updateFieldConfig(favoritesConfigUserUpdate);

    const favoritesConfigUpdate: MultiOptionConfig = { ...favoritesConfig};
    favoritesConfigUpdate.hidden = (isScopeUser ? true : false);
    this.detailsFormServices.dynamicFormService.updateFieldConfig(favoritesConfigUpdate);
    const overviewLayoutConfigUpdate: MultiOptionConfig = { ...overviewLayoutConfig };
    overviewLayoutConfigUpdate.hidden = (scope === EntityScopeEnum.Project);
    this.hasWideLabels$.next(!overviewLayoutConfigUpdate.hidden);
    this.detailsFormServices.dynamicFormService.updateFieldConfig(overviewLayoutConfigUpdate);
  }

  public async loadForm(editArgs: {Layout: Layout, Fields: LayoutFieldExt[], Filters: LayoutFilter[], IsNew: boolean, project: Project, user: User, hasReferences: boolean}) {
    this.editArgs = editArgs;
    this.layout = editArgs.Layout;
    this.detailsFormServices.dynamicFormService.updateFieldValue(overviewLayoutConfig.name, {
      isOverviewLayout: this.layout.IsOverviewLayout || false
    });
    this.detailsFormServices.dynamicFormService.updateFieldValue(nameConfig.name, this.layout.Name);
    this.detailsFormServices.dynamicFormService.updateFieldValue(descriptionConfig.name, this.layout.Description);

    this.detailsFormServices.dynamicFormService.updateFieldConfig({
      // readonly: editArgs.hasReferences,
      name: typeConfig.name,
      source: await this.getScopes()
    } as any);
    this.detailsFormServices.dynamicFormService.updateFieldValue(typeConfig.name, this.layout.Scope);

    const visibleFor: OptionViewPointValue = {
      co: this.layout.IsVisibleForCO,
      ct: this.layout.IsVisibleForCT,
      sc: this.layout.IsVisibleForSC,
    };
    this.detailsFormServices.dynamicFormService.updateFieldValue(visibleForConfig.name, visibleFor);

    const visibleForConfigUpdate: MultiOptionConfig = { ...visibleForConfig};
    visibleForConfigUpdate.hidden = !this.hasEditLayoutVisibility || this.layout.Scope === EntityScopeEnum.User;
    this.detailsFormServices.dynamicFormService.updateFieldConfig(visibleForConfigUpdate);

    const isScopeUser: boolean = (this.layout.Scope === +EntityScopeEnum.User);
    this.setFavoriteUserValue(isScopeUser);
    this.setFavoriteUserConfig(isScopeUser);

    this.firstLoad = false;

    this.detailsFormServices.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this))
      .subscribe((param: any) =>
      {
        if (param.config.name === 'type') {
          if (this.editArgs.hasReferences && param.value !== +EntityScopeEnum.Global) {
            this.dialogService.showNotificationDialog({
              title: this.translateService.instant('MM.layout.messageChangeLayoutTypeTitle'),
              content: this.translateService.instant('MM.layout.changeLayoutTypeHasRefsErrorMessage')
            });

            this.detailsFormServices.dynamicFormService.updateFieldValue(typeConfig.name, EntityScopeEnum.Global);
          }
          else {
            const isScopeUserNew: boolean = (param.value === +EntityScopeEnum.User);

            // Update visibleForConfig visibility
            const visibleForConfigUpdate: MultiOptionConfig = { ...visibleForConfig};
            visibleForConfigUpdate.hidden = !this.hasEditLayoutVisibility || isScopeUserNew;
            this.detailsFormServices.dynamicFormService.updateFieldConfig(visibleForConfigUpdate);

            if (param.value === EntityScopeEnum.Project) {
              this.detailsFormServices.dynamicFormService.updateFieldValue(overviewLayoutConfig.name, {isOverviewLayout: false});
              this.onOverviewLayoutChanged.next(false);

              const overviewLayoutConfigUpdate: MultiOptionConfig = { ...overviewLayoutConfig };
              overviewLayoutConfigUpdate.hidden = true;
              this.hasWideLabels$.next(false);
              this.detailsFormServices.dynamicFormService.updateFieldConfig(overviewLayoutConfigUpdate);
            } else {
              const overviewLayoutConfigUpdate: MultiOptionConfig = { ...overviewLayoutConfig };
              overviewLayoutConfigUpdate.hidden = false;
              this.hasWideLabels$.next(true);
              this.detailsFormServices.dynamicFormService.updateFieldConfig(overviewLayoutConfigUpdate);
            }

            this.setFavoriteUserValue(isScopeUserNew);
            this.setFavoriteUserConfig(isScopeUserNew);
            this.onTypeChanged.next(param.value);
          }
        }
        if (param.config.name === 'isOverviewLayout') {

          const isOverviewLayout = param.value.isOverviewLayout;
          this.onOverviewLayoutChanged.next(isOverviewLayout);
        }

        this.commit();
      });

    this.detailsFormServices?.dynamicFormValidationService.formErrors$
      .pipe(untilDestroyed(this))
      .subscribe(err => {
        this.hasErrorsSubject.next((Object.keys(err).length === 0 ? false : true));
      });
  }

  setFavoriteUserConfig(isScopeUser: boolean) {
    const favoritesConfigUserUpdate: MultiOptionConfig = {...favoritesConfigUser};
    favoritesConfigUserUpdate.hidden = (isScopeUser ? false : true);
    this.detailsFormServices.dynamicFormService.updateFieldConfig(favoritesConfigUserUpdate);

    const favoritesConfigUpdate: MultiOptionConfig = { ...favoritesConfig};
    favoritesConfigUpdate.hidden = (isScopeUser ? true : false);
    this.detailsFormServices.dynamicFormService.updateFieldConfig(favoritesConfigUpdate);
  }

  setFavoriteUserValue(isScopeUser: boolean) {
    if (isScopeUser) {
      const favoriteForUser: OptionFavoriteValue = {
        favoriteUser: this.layout.IsFavoriteUser,
      };
      this.detailsFormServices.dynamicFormService.updateFieldValue(favoritesConfigUser.name, favoriteForUser);
    }
    else {
      const favoriteFor: OptionFavoriteValue = {
        favoriteUser: this.layout.IsFavoriteUser,
        favoriteAll: (this.layout.Scope === EntityScopeEnum.User ? false : this.layout.IsFavoriteAll)
      };
      this.detailsFormServices.dynamicFormService.updateFieldValue(favoritesConfig.name, favoriteFor);
    }
  }

  public validate(): boolean {
    return this.detailsFormServices?.dynamicFormValidationService.validate();
  }

  commit() {
    let changed = false;

    const nameValue = this.detailsFormServices.dynamicFormService.getFieldValue(nameConfig.name);
    const descriptionValue = this.detailsFormServices.dynamicFormService.getFieldValue(descriptionConfig.name);
    const typeValue = this.detailsFormServices.dynamicFormService.getFieldValue(typeConfig.name);
    const visibleValue: { co: boolean, ct: boolean, sc: boolean} = this.detailsFormServices.dynamicFormService.getFieldValue(visibleForConfig.name);
    const isOverviewLayoutValue = this.detailsFormServices.dynamicFormService.getFieldValue(overviewLayoutConfig.name);
    const isOverviewLayout = isOverviewLayoutValue?.isOverviewLayout || false;

    if (this.layout.IsOverviewLayout !== isOverviewLayout) {
      this.layout.IsOverviewLayout = isOverviewLayout;
      changed = true;
    }
    if (this.layout.Name !== nameValue) {
      this.layout.Name = nameValue;
      changed = true;
    }

    if (this.layout.Description !== descriptionValue) {
      this.layout.Description = this.detailsFormServices.dynamicFormService.getFieldValue(descriptionConfig.name);
      changed = true;
    }

    if (this.layout.Scope !== typeValue) {
      if (typeValue === EntityScopeEnum.Global) {
        this.layout.Project = null;
        this.layout.User = null;
        this.layout.Scope = EntityScopeEnum.Global;
        changed = true;
      }
      else if (typeValue === EntityScopeEnum.Project) {
        this.layout.Project = new Project({ Id: this.editArgs.project.Id});
        this.layout.User = null;
        this.layout.Scope = EntityScopeEnum.Project;
        changed = true;
      }
      else if (typeValue === EntityScopeEnum.User) {
        this.layout.Project = null;
        this.layout.User = new User({Id: this.editArgs.user.Id});
        this.layout.Scope = EntityScopeEnum.User;
        changed = true;
      }
    }

    let favoritValueUser: { favoriteUser: boolean};
    let favoritValue: { favoriteUser: boolean, favoriteAll: boolean};
    const isScopeUser: boolean = (this.layout.Scope === +EntityScopeEnum.User);
    if (isScopeUser) {
      favoritValueUser = this.detailsFormServices.dynamicFormService.getFieldValue(favoritesConfigUser.name);
    }
    else {
      favoritValue = this.detailsFormServices.dynamicFormService.getFieldValue(favoritesConfig.name);
    }



    if (visibleValue.co !== this.layout.IsVisibleForCO) {
      this.layout.IsVisibleForCO = visibleValue.co;
      changed = true;
    }

    if (visibleValue.ct !== this.layout.IsVisibleForCT) {
      this.layout.IsVisibleForCT = visibleValue.ct;
      changed = true;
    }

    if (visibleValue.sc !== this.layout.IsVisibleForSC) {
      this.layout.IsVisibleForSC = visibleValue.sc;
      changed = true;
    }

    if (!isScopeUser && favoritValue.favoriteAll !== this.layout.IsFavoriteAll) {
      this.layout.IsFavoriteAll = (favoritValue.favoriteAll ?? false);
      changed = true;
    }

    if (isScopeUser && favoritValueUser.favoriteUser !== this.layout.IsFavoriteUser) {
      this.layout.IsFavoriteUser = favoritValueUser.favoriteUser;
      changed = true;
    }
    else if (!isScopeUser && favoritValue.favoriteUser !== this.layout.IsFavoriteUser) {
      this.layout.IsFavoriteUser = favoritValue.favoriteUser;
      changed = true;
    }

    if (changed) {
      this.valueChanged.next(null);
    }
  }

  private async getScopes(): Promise<SelectItem[]> {

    return firstValueFrom(await combineLatest([this.permissionEditGlobalLayout$, this.permissionEditProjectLayout$])
    .pipe(untilDestroyed(this),
      map(([hasGlobalLayoutPerm, hasProjectLayoutPerm]) => {
        const source: SelectItem[] = [];

        Object.keys(EntityScopeEnum)
        .filter(key =>  !isNaN(Number(EntityScopeEnum[key])) && (EntityScopeEnum[key] === EntityScopeEnum.Global && hasGlobalLayoutPerm || EntityScopeEnum[key] === EntityScopeEnum.Project && hasProjectLayoutPerm || EntityScopeEnum[key] === EntityScopeEnum.User)
        )
        .forEach(key => {
          source.push({
            id: EntityScopeEnum[key],
            text: this.translateService.instant('MM.layout.details.type' + key)
          });
        });

        return source;
      })
    ));
  }


}

